import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Grid,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Checkbox,
  FormGroup,
  Switch,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  Clear,
  Description,
  Image as ImageIcon,
  Visibility,
  Folder,
  TableChart,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import {
  FaAws,
  FaImage,
  FaFileExcel,
  FaFolder,
  FaGlobeAmericas,
  FaGlobeEurope,
  FaDownload,
  FaCloud,
  FaChevronLeft,
  FaChevronRight,
  FaTimes
} from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import RelatedToolsBar from '../components/RelatedToolsBar';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

// Dimensions per size, mirroring ImageResizeHelper.DimensionsForSize on the
// backend. Original is the source image's own size (no resize).
const SIZE_DIMENSION_LABELS = {
  Original:  'Source dimensions (no resize)',
  Zoom:      '1200 × 1200 px',
  Large:     '600 × 600 px',
  Small:     '300 × 300 px',
  Thumbnail: '150 × 150 px',
};

const AWSImageLinks = () => {
  const theme = useTheme();
  const imageFilesInputRef = useRef(null);
  const excelFileInputRef = useRef(null);
  const otherFilesInputRef = useRef(null);

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [selectedTab, setSelectedTab] = useState(0);
  const [imageFiles, setImageFiles] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [otherFiles, setOtherFiles] = useState([]);
  const [imageType, setImageType] = useState('Item');
  const [region, setRegion] = useState('US');
  const [imageSizes, setImageSizes] = useState({
    Original: true,
    Zoom: false,
    Large: false,
    Small: false,
    Thumbnail: false
  });
  const [resizeSquare, setResizeSquare] = useState(false);
  // Square mode replaces resizeSquare incrementally. We still send the
  // bool for backwards compatibility, but the radio also picks a mode
  // ("none" / "square" / "squareBorder"). Border = legacy 15% white
  // margin (chkSquareWithWhiteBorder from the WinForms tool).
  const [squareMode, setSquareMode] = useState('none');
  const [customWidth,  setCustomWidth]  = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [maxKbMode,    setMaxKbMode]    = useState('off');
  const [maxKbPreset,  setMaxKbPreset]  = useState(400);
  const [maxKbCustom,  setMaxKbCustom]  = useState('');
  const effectiveMaxKb = (() => {
    if (maxKbMode === 'off') return 0;
    if (maxKbMode === 'preset') return maxKbPreset;
    const parsed = parseInt(maxKbCustom, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showLogic, setShowLogic] = useState(false);

  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const fetchStatus = async (processId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.AWS_IMAGE_LINKS_STATUS(processId));

      if (response.logs && response.logs.length > 0) {
        setLogs(processId, response.logs);
      }

      if (response.progressPercentage !== undefined) {
        setProgress(response.progressPercentage);
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(processId, response.fileName);
        }

        setResults({
          success: true,
          fileCount: response.processedItems || 0,
          fileName: response.fileName
        });
        completeActivityProcess(processId, 'success', 'Upload completed successfully');
        setSuccess('Files uploaded successfully and Excel file generated!');
        setLoading(false);
        setProgress(null);
      } else if (response.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }
        setError(`Error: ${response.message}`);
        setLoading(false);
        setProgress(null);
        completeActivityProcess(processId, 'error', response.message);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, serverFileName) => {
    try {
      addLog(processId, 'Downloading file...');

      const response = await fetch(API_ENDPOINTS.AWS_IMAGE_LINKS_DOWNLOAD(processId), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || `AWS_Links_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(processId, 'Download complete!');
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, `Download error: ${err.message}`);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setError('');
    setSuccess('');
    setResults(null);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (type === 'images') {
      const validFiles = droppedFiles.filter(file => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
        return validTypes.includes(file.type);
      });
      if (validFiles.length > 0) {
        setImageFiles(validFiles);
        setError('');
      } else {
        setError('Please drop image files only (JPG, PNG, GIF, BMP)');
      }
    } else if (type === 'excel') {
      const file = droppedFiles[0];
      if (file && file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
        setExcelFile(file);
        setError('');
      } else {
        setError('Please drop an Excel file only (.xlsx, .xls)');
      }
    } else if (type === 'files') {
      setOtherFiles(droppedFiles);
      setError('');
    }
  };

  const handleImageFilesSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      setError('Some files were filtered out. Only image files (JPG, PNG, GIF, BMP) are allowed.');
    }

    setImageFiles(validFiles);
    setError('');
    setSuccess('');
  };

  const handleExcelFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
        setError('Please select an Excel file only.');
        return;
      }
      setExcelFile(file);
      setError('');
      setSuccess('');
    }
  };

  const handleOtherFilesSelect = (event) => {
    const files = Array.from(event.target.files);
    setOtherFiles(files);
    setError('');
    setSuccess('');
  };

  const handleImageSizeChange = (size) => {
    setImageSizes(prev => ({
      ...prev,
      [size]: !prev[size]
    }));
  };

  const handleUploadImages = async () => {
    if (imageFiles.length === 0) {
      setError('Please select image files first.');
      return;
    }

    const selectedSizes = Object.keys(imageSizes).filter(size => imageSizes[size]);
    if (selectedSizes.length === 0) {
      setError('Please select at least one image size.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      imageFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('imageType', imageType);
      formData.append('region', region);
      formData.append('imageSizes', selectedSizes.join(','));
      formData.append('resizeSquare', resizeSquare);
      formData.append('squareMode',   squareMode);
      formData.append('customWidth',  String(parseInt(customWidth,  10) || 0));
      formData.append('customHeight', String(parseInt(customHeight, 10) || 0));
      formData.append('maxKb',        String(effectiveMaxKb));

      const response = await apiCall(API_ENDPOINTS.AWS_IMAGE_LINKS_UPLOAD_IMAGES, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addActivityProcess(serverProcessId, `Uploading ${imageFiles.length} images to AWS`, 'AWS Image Links');
        addLog(serverProcessId, 'Starting image upload...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'An error occurred during image upload. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  // Download a pre-populated Excel template (ItemID column + a few
  // sample rows) so the user has something to start from on the Excel
  // Import tab. Mirrors AWSImageLinksAlternate.handleDownloadSample.
  const handleDownloadSample = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.AWS_IMAGE_LINKS_SAMPLE, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
      });
      if (!response.ok) throw new Error('Failed to download sample file');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      });
      link.download = `AWS Image Links Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download sample file.');
    }
  };

  const handleProcessExcel = async () => {
    if (!excelFile) {
      setError('Please select an Excel file first.');
      return;
    }

    const selectedSizes = Object.keys(imageSizes).filter(size => imageSizes[size]);
    if (selectedSizes.length === 0) {
      setError('Please select at least one image size.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('imageType', imageType);
      formData.append('region', region);
      formData.append('imageSizes', selectedSizes.join(','));
      // Legacy bool kept for back-compat; squareMode supersedes it on
      // the server. Sending both is harmless since the server prefers
      // squareMode when it's non-empty.
      formData.append('resizeSquare', resizeSquare);
      formData.append('squareMode',   squareMode);
      formData.append('customWidth',  String(parseInt(customWidth,  10) || 0));
      formData.append('customHeight', String(parseInt(customHeight, 10) || 0));
      formData.append('maxKb',        String(effectiveMaxKb));

      const response = await apiCall(API_ENDPOINTS.AWS_IMAGE_LINKS_PROCESS_EXCEL, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addActivityProcess(serverProcessId, 'Processing Excel images', 'AWS Image Links');
        addLog(serverProcessId, 'Starting Excel processing...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred during Excel processing. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  const handleUploadFiles = async () => {
    if (otherFiles.length === 0) {
      setError('Please select files first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      otherFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('region', region);

      const response = await apiCall(API_ENDPOINTS.AWS_IMAGE_LINKS_UPLOAD_FILES, {
        method: 'POST',
        body: formData
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addActivityProcess(serverProcessId, `Uploading ${otherFiles.length} files to AWS`, 'AWS Image Links');
        addLog(serverProcessId, 'Starting file upload...');

        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'An error occurred during file upload. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  const resetForm = () => {
    setImageFiles([]);
    setExcelFile(null);
    setOtherFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(null);
    if (imageFilesInputRef.current) imageFilesInputRef.current.value = '';
    if (excelFileInputRef.current) excelFileInputRef.current.value = '';
    if (otherFilesInputRef.current) otherFilesInputRef.current.value = '';
  };

  const getTabDescription = () => {
    switch (selectedTab) {
      case 0:
        return 'Upload images directly to AWS S3 and generate links in multiple sizes';
      case 1:
        return 'Import image paths from Excel file and generate AWS links';
      case 2:
        return 'Upload any files (PDFs, documents, etc.) to AWS S3';
      default:
        return '';
    }
  };

  const hasFiles = () => {
    switch (selectedTab) {
      case 0: return imageFiles.length > 0;
      case 1: return excelFile !== null;
      case 2: return otherFiles.length > 0;
      default: return false;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = (index) => {
    if (selectedTab === 0) {
      setImageFiles(prev => prev.filter((_, i) => i !== index));
    } else if (selectedTab === 1) {
      setExcelFile(null);
    } else {
      setOtherFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const getCurrentFiles = () => {
    switch (selectedTab) {
      case 0: return imageFiles;
      case 1: return excelFile ? [excelFile] : [];
      case 2: return otherFiles;
      default: return [];
    }
  };

  const isImageFile = (file) => {
    return file.type && file.type.startsWith('image/');
  };

  const previewUrls = useMemo(() => {
    const files = selectedTab === 0 ? imageFiles : selectedTab === 1 ? (excelFile ? [excelFile] : []) : otherFiles;
    return files.map(file => ({
      src: URL.createObjectURL(file),
      label: file.name,
      size: file.size,
      isImage: file.type && file.type.startsWith('image/'),
      type: file.type,
    }));
  }, [imageFiles, excelFile, otherFiles, selectedTab]);

  const openPreview = (startIndex) => {
    setPreviewIndex(startIndex);
    setPreviewOpen(true);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!previewOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setPreviewIndex(prev => (prev - 1 + previewUrls.length) % previewUrls.length);
      } else if (e.key === 'ArrowRight') {
        setPreviewIndex(prev => (prev + 1) % previewUrls.length);
      } else if (e.key === 'Escape') {
        setPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewOpen, previewUrls.length]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="AWS Image Links" category="link" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          AWS IMAGE LINKS
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Upload & Generate Links
              </Typography>

              {/* Upload Type Tabs */}
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 3,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '& .MuiTabs-indicator': {
                    backgroundColor: TOOL_COLORS.LINK.primary,
                  },
                }}
              >
                <Tab
                  icon={<ImageIcon />}
                  label="Image Upload"
                  iconPosition="start"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': { color: TOOL_COLORS.LINK.primary },
                  }}
                />
                <Tab
                  icon={<TableChart />}
                  label="Excel Import"
                  iconPosition="start"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': { color: TOOL_COLORS.LINK.primary },
                  }}
                />
                <Tab
                  icon={<Folder />}
                  label="File Upload"
                  iconPosition="start"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': { color: TOOL_COLORS.LINK.primary },
                  }}
                />
              </Tabs>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                {getTabDescription()}
              </Typography>

              {/* Configuration Options (for Images and Excel tabs) */}
              {(selectedTab === 0 || selectedTab === 1) && (
                <Card sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${theme.palette.divider}` }} elevation={0}>
                  <CardContent>
                    <Grid container spacing={3}>
                      {/* Image Type */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                            Image Type
                          </FormLabel>
                          <RadioGroup
                            value={imageType}
                            onChange={(e) => setImageType(e.target.value)}
                          >
                            <FormControlLabel
                              value="Item"
                              control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                              label="Item Images"
                            />
                            <FormControlLabel
                              value="Design"
                              control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                              label="Design Images"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Region */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                            Region
                          </FormLabel>
                          <RadioGroup
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                          >
                            <FormControlLabel
                              value="US"
                              control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaGlobeAmericas size={14} /> US</Box>}
                            />
                            <FormControlLabel
                              value="EU"
                              control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaGlobeEurope size={14} /> EU</Box>}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Image Sizes */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                            Image Sizes
                          </FormLabel>
                          <FormGroup row>
                            {Object.keys(imageSizes).map((size) => {
                              const customActive = customWidth !== '' || customHeight !== '';
                              const tip = customActive
                                ? 'Disabled — custom W/H dimensions override the preset sizes. Clear them to use sizes again.'
                                : SIZE_DIMENSION_LABELS[size] || '';
                              return (
                                <Tooltip key={size} title={tip} arrow placement="top" disableInteractive>
                                  <span>
                                    <FormControlLabel
                                      disabled={customActive}
                                      control={
                                        <Checkbox
                                          checked={imageSizes[size]}
                                          onChange={() => handleImageSizeChange(size)}
                                          size="small"
                                          sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }}
                                        />
                                      }
                                      label={<Typography variant="body2">{size}</Typography>}
                                    />
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </FormGroup>
                          {(customWidth !== '' || customHeight !== '') && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                              Sizes disabled — custom W/H is driving the output.
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Square style — radio replaces the old single Square
                          switch. Border variant adds a 15% white margin
                          around the squared image. The legacy resizeSquare
                          bool is kept in sync below so back-compat is intact. */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                            Square Style
                          </FormLabel>
                          <RadioGroup
                            value={squareMode}
                            onChange={(e) => {
                              setSquareMode(e.target.value);
                              setResizeSquare(e.target.value !== 'none');
                            }}
                            sx={{ gap: 0.25 }}
                          >
                            {[
                              { value: 'none',         label: 'None' },
                              { value: 'square',       label: 'Square' },
                              { value: 'squareBorder', label: 'Square + Border' },
                            ].map((opt) => (
                              <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                                label={<Typography variant="body2">{opt.label}</Typography>}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Grid>

                      {/* Custom dimensions — overrides the preset Image Sizes. */}
                      <Grid item xs={12} md={3}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                            Custom Dimensions (px)
                          </FormLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <TextField
                              size="small"
                              placeholder="Width"
                              value={customWidth}
                              onChange={(e) => setCustomWidth(e.target.value.replace(/[^\d]/g, ''))}
                              InputProps={{ sx: { fontSize: '0.85rem' } }}
                              sx={{ width: 95 }}
                            />
                            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>×</Typography>
                            <TextField
                              size="small"
                              placeholder="Height"
                              value={customHeight}
                              onChange={(e) => setCustomHeight(e.target.value.replace(/[^\d]/g, ''))}
                              InputProps={{ sx: { fontSize: '0.85rem' } }}
                              sx={{ width: 95 }}
                            />
                            {(customWidth || customHeight) && (
                              <IconButton
                                size="small"
                                onClick={() => { setCustomWidth(''); setCustomHeight(''); }}
                                sx={{ p: 0.5 }}
                              >
                                <Clear sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.75 }}>
                            {customWidth || customHeight
                              ? 'Overrides preset sizes. Blank = mirror other.'
                              : 'Optional — leave blank to use Image Sizes.'}
                          </Typography>
                        </FormControl>
                      </Grid>

                      {/* Max File Size — iterative JPEG quality compression. */}
                      <Grid item xs={12}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                            Max File Size
                          </FormLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Chip
                              label="No limit"
                              size="small"
                              clickable
                              variant={maxKbMode === 'off' ? 'filled' : 'outlined'}
                              onClick={() => setMaxKbMode('off')}
                              sx={maxKbMode === 'off' ? { bgcolor: TOOL_COLORS.LINK.primary, color: '#fff' } : {}}
                            />
                            {[400, 600, 1000, 10200].map((kb) => {
                              const selected = maxKbMode === 'preset' && maxKbPreset === kb;
                              return (
                                <Chip
                                  key={kb}
                                  label={kb >= 1000 ? `${Math.round(kb / 1000)} MB` : `${kb} KB`}
                                  size="small"
                                  clickable
                                  variant={selected ? 'filled' : 'outlined'}
                                  onClick={() => { setMaxKbMode('preset'); setMaxKbPreset(kb); }}
                                  sx={selected ? { bgcolor: TOOL_COLORS.LINK.primary, color: '#fff' } : {}}
                                />
                              );
                            })}
                            <TextField
                              size="small"
                              placeholder="Custom"
                              value={maxKbCustom}
                              onFocus={() => setMaxKbMode('custom')}
                              onChange={(e) => {
                                const v = e.target.value.replace(/[^\d]/g, '');
                                setMaxKbCustom(v);
                                setMaxKbMode('custom');
                              }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">KB</InputAdornment>,
                                sx: {
                                  fontSize: '0.85rem',
                                  bgcolor: maxKbMode === 'custom' ? `${TOOL_COLORS.LINK.primary}10` : 'transparent',
                                },
                              }}
                              sx={{ width: 110 }}
                            />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 1 }}>
                              {effectiveMaxKb > 0 ? `≤ ${effectiveMaxKb} KB / image` : 'No file-size limit applied.'}
                            </Typography>
                          </Box>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Region selection for Files tab */}
              {selectedTab === 2 && (
                <Card sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${theme.palette.divider}` }} elevation={0}>
                  <CardContent>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 1 }}>
                        Select Region
                      </FormLabel>
                      <RadioGroup
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        row
                      >
                        <FormControlLabel
                          value="US"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaGlobeAmericas size={14} /> United States</Box>}
                        />
                        <FormControlLabel
                          value="EU"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.LINK.primary } }} />}
                          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaGlobeEurope size={14} /> European Union</Box>}
                        />
                      </RadioGroup>
                    </FormControl>
                  </CardContent>
                </Card>
              )}

              {/* File Upload Area */}
              <Card sx={{ mb: 3, bgcolor: hasFiles() ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.LINK.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {selectedTab === 0 ? 'Image Files' : selectedTab === 1 ? 'Excel File' : 'Files'}
                    </Typography>
                    {hasFiles() && (
                      <Tooltip title={`${getCurrentFiles().length} file(s) selected`}>
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden file inputs */}
                  <input
                    type="file"
                    ref={imageFilesInputRef}
                    onChange={handleImageFilesSelect}
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <input
                    type="file"
                    ref={excelFileInputRef}
                    onChange={handleExcelFileSelect}
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                  />
                  <input
                    type="file"
                    ref={otherFilesInputRef}
                    onChange={handleOtherFilesSelect}
                    multiple
                    style={{ display: 'none' }}
                  />

                  {/* Drag & Drop Zone */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, selectedTab === 0 ? 'images' : selectedTab === 1 ? 'excel' : 'files')}
                    onClick={() => {
                      if (selectedTab === 0) imageFilesInputRef.current?.click();
                      else if (selectedTab === 1) excelFileInputRef.current?.click();
                      else otherFilesInputRef.current?.click();
                    }}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.LINK.primary : hasFiles() ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.LINK.light
                        : hasFiles()
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: hasFiles() ? '#4CAF50' : TOOL_COLORS.LINK.primary,
                        bgcolor: hasFiles()
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.LINK.light,
                      }
                    }}
                  >
                    {!hasFiles() ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.LINK.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging
                            ? (selectedTab === 0 ? 'Drop images here' : selectedTab === 1 ? 'Drop Excel file here' : 'Drop files here')
                            : (selectedTab === 0 ? 'Drop your image files here or click to browse' : selectedTab === 1 ? 'Drop your Excel file here or click to browse' : 'Drop your files here or click to browse')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedTab === 0 ? 'JPG, PNG, GIF, BMP files supported' : selectedTab === 1 ? 'Excel files (.xlsx, .xls) only' : 'All file types supported'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {getCurrentFiles().length} file(s) selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedTab === 1 ? 'Click or drop to replace' : 'Click or drop more files to add'}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File List */}
                  {hasFiles() && (
                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                      {getCurrentFiles().map((file, index) => (
                        <Box
                          key={index}
                          onClick={(e) => { e.stopPropagation(); if (previewUrls[index]?.isImage) openPreview(index); }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            cursor: previewUrls[index]?.isImage ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                              borderColor: TOOL_COLORS.LINK.primary,
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            {previewUrls[index]?.isImage ? (
                              <Box
                                component="img"
                                src={previewUrls[index].src}
                                alt={file.name}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  objectFit: 'cover',
                                  borderRadius: 0.5,
                                  flexShrink: 0,
                                  border: `1px solid ${theme.palette.divider}`,
                                }}
                              />
                            ) : (
                              <Description sx={{ color: TOOL_COLORS.LINK.primary, fontSize: 20 }} />
                            )}
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(file.size)})
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            {previewUrls[index]?.isImage && (
                              <Tooltip title="Preview">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); openPreview(index); }}
                                  sx={{ color: TOOL_COLORS.LINK.primary }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={selectedTab === 0 ? handleUploadImages : selectedTab === 1 ? handleProcessExcel : handleUploadFiles}
                  disabled={!hasFiles() || (selectedTab !== 2 && Object.values(imageSizes).every(v => !v)) || loading}
                  sx={{
                    bgcolor: TOOL_COLORS.LINK.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                    px: 4
                  }}
                  startIcon={<FaCloud />}
                >
                  {loading ? 'Processing...' : selectedTab === 0 ? 'Upload Images' : selectedTab === 1 ? 'Process Excel' : 'Upload Files'}
                </Button>

                {/* Download Template — only on the Excel Import tab. A
                    starter Excel with one ItemID column + sample rows
                    helps users get the column shape right without
                    guessing. Mirrors the AWS File Links Alternate button. */}
                {selectedTab === 1 && (
                  <Button
                    variant="outlined"
                    onClick={handleDownloadSample}
                    disabled={loading}
                    sx={{
                      borderColor: TOOL_COLORS.LINK.primary,
                      color: TOOL_COLORS.LINK.primary,
                      '&:hover': {
                        borderColor: TOOL_COLORS.LINK.hover,
                        bgcolor: TOOL_COLORS.LINK.light,
                      },
                    }}
                    startIcon={<FaDownload />}
                  >
                    Download Template
                  </Button>
                )}

                {(hasFiles() || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Downloads Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {selectedTab === 0 ? 'Uploading images to AWS...' : selectedTab === 1 ? 'Processing Excel file...' : 'Uploading files...'}
                  </Typography>
                  <LinearProgress
                    variant={progress > 0 ? "determinate" : "indeterminate"}
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: TOOL_COLORS.LINK.primary,
                        borderRadius: 4,
                      }
                    }}
                  />
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaAws color={TOOL_COLORS.LINK.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.fileCount || 0} Files Uploaded`}
                        secondary="Successfully uploaded to AWS S3"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaFileExcel color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Excel File Generated"
                        secondary="Contains all AWS links"
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Excel file has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Image Sizes Info */}
              {(selectedTab === 0 || selectedTab === 1) && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Available Image Sizes:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                    {Object.keys(imageSizes).map((size) => (
                      <Tooltip key={size} title={SIZE_DIMENSION_LABELS[size] || ''} arrow placement="top" disableInteractive>
                        <Chip
                          label={size}
                          size="small"
                          variant={imageSizes[size] ? "filled" : "outlined"}
                          sx={{
                            fontSize: '0.7rem',
                            bgcolor: imageSizes[size] ? TOOL_COLORS.LINK.light : 'transparent',
                            borderColor: imageSizes[size] ? TOOL_COLORS.LINK.primary : theme.palette.divider,
                            color: imageSizes[size] ? TOOL_COLORS.LINK.primary : theme.palette.text.secondary,
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </>
              )}

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                {selectedTab === 0 && (
                  <>
                    1. Select image type (Item or Design)<br />
                    2. Choose target region (US or EU)<br />
                    3. Select desired image sizes<br />
                    4. Upload image files<br />
                    5. Download Excel with AWS links
                  </>
                )}
                {selectedTab === 1 && (
                  <>
                    1. Configure image settings<br />
                    2. Upload Excel file with image paths<br />
                    3. System processes images from paths<br />
                    4. Download Excel with AWS links
                  </>
                )}
                {selectedTab === 2 && (
                  <>
                    1. Select target region<br />
                    2. Upload any files (PDF, docs, etc.)<br />
                    3. Files are uploaded to AWS S3<br />
                    4. Download Excel with file links
                  </>
                )}
              </Typography>

              {/* How it works — grouping logic (image + excel upload only) */}
              {(selectedTab === 0 || selectedTab === 1) && (
                <>
                  <Divider sx={{ my: 3 }} />

                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: showLogic ? 2 : 0 }}
                    onClick={() => setShowLogic(!showLogic)}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                      How it works
                    </Typography>
                    {showLogic ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </Box>

                  <Collapse in={showLogic}>
                    <Box sx={{ fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.7, '& strong': { color: theme.palette.text.primary } }}>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Two Excel files are produced: a <strong>listing</strong> file (one row per item with its main image link) and a <strong>detail</strong> file that pivots every image found for that item into its own labelled column. Here's how an item's images are grouped into those columns.
                      </Typography>

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                        1. ItemID parsing
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Each ItemID is split into a <strong>base</strong> and a <strong>size</strong> at the last dash &mdash; e.g. "ABT141A-6" &rarr; base "ABT141A", size "6". The base column in the listing file shows the exact ItemID you imported (the one the link is for).
                      </Typography>

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                        2. Sibling-size gathering (same shape group only)
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        The item's own size is matched first. If a column would otherwise be empty, the tool borrows from <strong>interchangeable sibling sizes in the same shape group</strong>. Sizes outside the group (a different shape) are never used &mdash; so a "-6" import will not pull "-6R" (round) or "-6SQ" (square). The size groups are:
                      </Typography>
                      <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 220, overflow: 'auto' }}>
                        <strong>Accent:</strong> 2, 24<br />
                        <strong>Runner:</strong> 26, 27, 28, 29, 210&hellip;222<br />
                        <strong>Small rect:</strong> 3, 4<br />
                        <strong>Mid rect:</strong> 5, 6<br />
                        <strong>Large rect:</strong> 7, 8, 9, 10, 1115, 1215, 1218<br />
                        <strong>Round:</strong> 2R, 3R, 4R &nbsp;|&nbsp; 5R, 6R &nbsp;|&nbsp; 7R, 8R<br />
                        <strong>Oval:</strong> 3OV, 4OV &nbsp;|&nbsp; 5OV, 6OV &nbsp;|&nbsp; 7OV, 8OV<br />
                        <strong>Square:</strong> 3SQ, 4SQ &nbsp;|&nbsp; 5SQ, 6SQ &nbsp;|&nbsp; 7SQ, 8SQ
                      </Box>

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                        3. Column classification (first match wins)
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Every image found is dropped into a column based on the keyword in its filename. The checks run in this priority order, so the most specific label wins:
                      </Typography>
                      <Box sx={{ pl: 1, mb: 1, fontSize: 11, fontFamily: 'monospace', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 1, p: 1.5, maxHeight: 280, overflow: 'auto' }}>
                        Filename = the ItemID &rarr; <strong>ItemImage</strong> (main)<br />
                        -ICON / -GROUP &rarr; Other1&hellip;10<br />
                        COMPOSITE + ROOM &rarr; COMPOSITE<br />
                        BACK-ROOM &rarr; BACK<br />
                        DETAIL + ROOM &rarr; DETAIL10&hellip;DETAIL1 / DETAIL<br />
                        ROOMAI10&hellip;ROOMAI &rarr; matching ROOMAI column<br />
                        3DROOMBACK/FRONT/TOP/FLOOR, 3DROOM &rarr; respective<br />
                        ROOMBACK, ROOMFRONT, ROOMMAIN, ROOMTOP, ROOMFLOOR &rarr; respective<br />
                        ROOM1&hellip;4, WASHROOM, ROOM &rarr; respective<br />
                        Special OAK collection keywords &rarr; Special OAK<br />
                        DETAIL10&hellip;DETAIL1, DETAIL &rarr; respective<br />
                        COMPOSITE, DIM &rarr; respective<br />
                        FRONT-NS, FRONT, TOP, FOLD &rarr; respective<br />
                        BACK4&hellip;BACK1, BACKSIDE, BACK &rarr; respective<br />
                        CORNER (CRN/CRNR), CENTER (CEN), SWATCH &rarr; respective<br />
                        SIDE / SIDE-ALT / SIDE-OPEN, MAIN, CU, ALT, UGC &rarr; respective<br />
                        FLOOR, LEG, ARM, OPEN, COLLAPSED &rarr; respective<br />
                        ON / OFF, ANGLE, HARVEST, FROST, PAIR, DOUBLE, HAND &rarr; respective<br />
                        anything else &rarr; Other1&hellip;10
                      </Box>

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                        4. Family overflow
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        If a classified column is already filled, the image spills into the next free slot of its family &mdash; DETAIL &rarr; DETAIL1&hellip;DETAIL10, BACK &rarr; BACK1&hellip;BACK4 &mdash; and finally into an <strong>Other</strong> slot if the family is full. The main <strong>ItemImage</strong> falls back to a FRONT or ON shot if no exact item image exists.
                      </Typography>
                    </Box>
                  </Collapse>
                </>
              )}
            </Paper>

            {/* "Next steps" — sits directly below the Status & Downloads
                paper in the same right column. Follow-up tools (bulk
                download / link check) are one click away once the Excel
                lands, without crowding the status panel itself. */}
            <RelatedToolsBar layout="stack" />
          </Grid>
        </Grid>
      </Box>

      {/* Image Preview Lightbox */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)' } } }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', top: -40, right: 0, color: 'white', zIndex: 1 }}>
            <FaTimes size={20} />
          </IconButton>

          {previewUrls.length > 1 && (
            <IconButton
              onClick={() => setPreviewIndex(prev => (prev - 1 + previewUrls.length) % previewUrls.length)}
              sx={{ position: 'absolute', left: -60, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <FaChevronLeft size={24} />
            </IconButton>
          )}

          {previewUrls[previewIndex] && (
            <Box sx={{ textAlign: 'center' }}>
              {previewUrls[previewIndex].isImage ? (
                <img src={previewUrls[previewIndex].src} alt={previewUrls[previewIndex].label}
                  style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }} />
              ) : (
                <Box sx={{ py: 6, px: 4 }}>
                  <Description sx={{ fontSize: 64, color: 'rgba(255,255,255,0.5)', mb: 2 }} />
                  <Typography sx={{ color: 'white', fontSize: '1rem', mb: 0.5 }}>{previewUrls[previewIndex].label}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                    {formatFileSize(previewUrls[previewIndex].size)} &bull; {previewUrls[previewIndex].type || 'Unknown type'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', mt: 1 }}>
                    Preview not available for this file type
                  </Typography>
                </Box>
              )}
              <Typography sx={{ color: 'white', mt: 1, fontSize: '0.9rem' }}>
                {previewUrls[previewIndex].label}
                {previewUrls.length > 1 && ` (${previewIndex + 1}/${previewUrls.length})`}
              </Typography>
            </Box>
          )}

          {previewUrls.length > 1 && (
            <IconButton
              onClick={() => setPreviewIndex(prev => (prev + 1) % previewUrls.length)}
              sx={{ position: 'absolute', right: -60, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <FaChevronRight size={24} />
            </IconButton>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default AWSImageLinks;
